import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { resetPassword as resetPasswordApi } from "@/api/auth";
import { isApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const resetSchema = z
  .object({
    password: z.string().min(12),
    passwordConfirm: z.string().min(1),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ["passwordConfirm"],
    message: "mismatch",
  });

type ResetValues = z.infer<typeof resetSchema>;

/**
 * Три закрытых состояния ссылки приходят разными кодами, и каждое требует
 * своей подсказки: «уже использована» значит «иди к администратору за
 * новой», «истекла» — то же, но с другой причиной, «не найдена» — скорее
 * всего адрес скопирован не целиком. Общий текст вида «что-то пошло не
 * так» заставил бы человека гадать.
 */
function mapResetError(err: unknown, t: (key: string) => string): string {
  if (isApiError(err)) {
    if (err.status === 409) return t("passwordReset.errorUsed");
    if (err.status === 410) return t("passwordReset.errorExpired");
    if (err.status === 404) return t("passwordReset.errorNotFound");
  }
  return t("passwordReset.errorGeneric");
}

export interface ResetPasswordFormProps {
  token: string;
}

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { t } = useTranslation();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: "", passwordConfirm: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await resetPasswordApi({ token, password: values.password });
      setDone(true);
    } catch (err) {
      setFormError(mapResetError(err, t));
    }
  });

  if (done) {
    return (
      <Card className="mx-auto w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("passwordReset.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p data-testid="password-reset-done">{t("passwordReset.done")}</p>
          <Button asChild>
            <Link to="/login">{t("passwordReset.toLogin")}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("passwordReset.title")}</CardTitle>
        <CardDescription>{t("passwordReset.ruleLength")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reset-password">
              {t("passwordReset.password")}
            </Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.password ? true : undefined}
              {...register("password")}
            />
            {errors.password ? (
              <p role="alert" className="text-sm text-destructive">
                {t("passwordReset.ruleLength")}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reset-password-confirm">
              {t("passwordReset.repeat")}
            </Label>
            <Input
              id="reset-password-confirm"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.passwordConfirm ? true : undefined}
              {...register("passwordConfirm")}
            />
            {errors.passwordConfirm ? (
              <p role="alert" className="text-sm text-destructive">
                {t("passwordReset.ruleMatch")}
              </p>
            ) : null}
          </div>
          {formError !== null ? (
            <p
              role="alert"
              className="text-sm text-destructive"
              data-testid="password-reset-error"
            >
              {formError}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="password-reset-submit"
          >
            {t("passwordReset.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
